import React, { useState, useEffect, useRef, createContext, useContext } from "react";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { API } from "@/lib/api";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  const justLoggedInRef = useRef(false);

  const checkAuth = async (retryCount = 0, force = false) => {
    if (justLoggedInRef.current && !force) {
      return;
    }
    try {
      const response = await fetch(`${API}/auth/me`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setUser(data);
      } else {
        if (!justLoggedInRef.current) {
          setUser(null);
        }
      }
    } catch (error) {
      if (retryCount < 2) {
        await new Promise(r => setTimeout(r, 1000));
        return checkAuth(retryCount + 1, force);
      }
      if (!justLoggedInRef.current) {
        setUser(null);
      }
    } finally {
      setLoading(false);
      setAuthChecked(true);
    }
  };

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=') || window.location.search?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, []);

  const login = (userData) => {
    justLoggedInRef.current = true;
    setUser(userData);
    setLoading(false);
    setAuthChecked(true);
    setTimeout(() => { justLoggedInRef.current = false; }, 5000);
  };

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    } catch (error) {}
    justLoggedInRef.current = false;
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth, authChecked }}>
      {children}
    </AuthContext.Provider>
  );
};

import OnboardingModal from "../components/OnboardingModal";

export const ProtectedRoute = ({ children, adminOnly = false, requireSubscription = true }) => {
  const { user, loading, checkAuth } = useAuth();
  const location = useLocation();
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    if (user && !user.onboarding_complete && user.role !== "admin" && user.role !== "coach") {
      setShowOnboarding(true);
    }
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (adminOnly && user.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }

  const isAdminOrCoach = user.role === "admin" || user.role === "coach";
  const noSubscriptionPages = ["/pricing", "/billing", "/settings", "/subscription/success"];
  const isExemptPage = noSubscriptionPages.some(page => location.pathname.startsWith(page));

  if (requireSubscription && !isExemptPage && !isAdminOrCoach) {
    const subscriptionTier = user?.subscription_tier;
    const hasActiveSubscription = subscriptionTier &&
      subscriptionTier !== "free" &&
      subscriptionTier !== "none" &&
      subscriptionTier !== "";

    if (!hasActiveSubscription) {
      return <Navigate to="/pricing" state={{ from: location }} replace />;
    }
  }

  return (
    <>
      {showOnboarding && (
        <OnboardingModal onComplete={() => { setShowOnboarding(false); checkAuth(0, true); }} />
      )}
      {children}
    </>
  );
};

export const AuthCallback = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      const hash = window.location.hash;
      const search = window.location.search;
      const fullUrl = window.location.href;
      const params = new URLSearchParams(search);

      let sessionId = null;

      if (hash.includes("session_id=")) {
        sessionId = hash.split("session_id=")[1]?.split("&")[0];
      }
      if (!sessionId) {
        sessionId = params.get("session_id");
      }
      if (!sessionId && hash.includes("?")) {
        const hashParams = new URLSearchParams(hash.split("?")[1]);
        sessionId = hashParams.get("session_id");
      }
      if (!sessionId && fullUrl.includes("session_id=")) {
        const match = fullUrl.match(/session_id=([^&]+)/);
        if (match) sessionId = match[1];
      }

      if (sessionId) {
        console.log("[Auth] Processing session_id:", sessionId.substring(0, 20) + "...");
        console.log("[Auth] API URL:", API);
        try {
          // Retry up to 2 times with increasing delays
          let lastError = null;
          for (let attempt = 0; attempt < 3; attempt++) {
            try {
              if (attempt > 0) {
                console.log(`[Auth] Retry attempt ${attempt + 1}...`);
                await new Promise(r => setTimeout(r, attempt * 1500));
              }
              const response = await fetch(`${API}/auth/session`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ session_id: sessionId }),
              });

              console.log(`[Auth] Response status: ${response.status}`);

              if (response.ok) {
                const data = await response.json();
                login(data.user);

                const isAdminOrCoach = data.user?.role === "admin" || data.user?.role === "coach";
                const subscriptionTier = data.user?.subscription_tier;
                const hasActiveSubscription = subscriptionTier &&
                  subscriptionTier !== "free" &&
                  subscriptionTier !== "none" &&
                  subscriptionTier !== "";

                if (isAdminOrCoach || hasActiveSubscription) {
                  toast.success("Welcome back!");
                  window.history.replaceState({}, document.title, "/dashboard");
                  navigate("/dashboard", { replace: true });
                } else {
                  toast.info("Please choose a membership plan to continue");
                  window.history.replaceState({}, document.title, "/pricing");
                  navigate("/pricing", { replace: true });
                }
                return; // Success — exit the function
              } else {
                const errorData = await response.json().catch(() => ({}));
                lastError = errorData.detail || `Authentication failed (${response.status})`;
                // On 503 (service unavailable), retry; on 4xx, stop retrying
                if (response.status < 500) break;
              }
            } catch (fetchError) {
              lastError = fetchError.message || "Network error";
              console.error(`[Auth] Attempt ${attempt + 1} failed:`, fetchError);
            }
          }
          // All attempts failed
          toast.error(lastError || "Authentication failed. Please try again.");
          navigate("/login", { replace: true });
        } catch (error) {
          console.error("[Auth] Session exchange failed:", error);
          toast.error("Authentication error. Please try again.");
          navigate("/login", { replace: true });
        }
      } else {
        toast.error("No authentication data received. Please try again.");
        navigate("/login", { replace: true });
      }
    };

    processSession();
  }, []);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-text-secondary">Completing sign in...</p>
      </div>
    </div>
  );
};

export default AuthContext;
